import { z } from "zod";
import { deviceInfoSchema } from "./capture";
import { MAX_VIDEO_MS } from "./limits";

/** A JavaScript Date can hold +-8.64e15 ms; capture times are positive epoch milliseconds. */
const epochMs = z.number().int().min(0).max(8.64e15);

export const reportKindSchema = z.enum(["replay", "screenshot", "video"]);
/** FR-SUB-01 */
export const prioritySchema = z.enum(["low", "medium", "high", "urgent"]);
export const reportStatusSchema = z.enum(["open", "in_progress", "resolved", "closed", "expired"]);

const contextValueSchema = z.union([z.string().max(1000), z.number(), z.boolean()]);

/**
 * The `payload` part of a report upload: everything except the files. Capture data (replay,
 * console, network, actions) travels as separate compressed files (media-and-compression.md).
 */
export const reportPayloadSchema = z
  .object({
    kind: reportKindSchema,
    title: z.string().trim().min(1, "Add a title").max(200),
    description: z.string().trim().max(5000).optional(),
    priority: prioritySchema.default("medium"),
    /** FR-SUB-01: ask for a Jira issue now (P6). */
    createJiraIssue: z.boolean().optional(),
    pageUrl: z.string().max(4096),
    pageTitle: z.string().max(500).default(""),
    device: deviceInfoSchema,
    /** FR-CAP-06 */
    context: z.record(z.string().max(100), contextValueSchema).default({}),
    capture: z
      .object({
        startedAt: epochMs,
        endedAt: epochMs,
        truncatedReplay: z.boolean().optional(),
        captureErrors: z.number().int().nonnegative().optional(),
      })
      .optional(),
    /** FR-VID-03: trim is stored as a range, never re-encoded. */
    video: z
      .object({
        durationMs: z.number().int().positive().max(MAX_VIDEO_MS),
        trimStartMs: z.number().int().nonnegative().optional(),
        trimEndMs: z.number().int().positive().optional(),
      })
      .optional(),
    client: z.object({ name: z.enum(["extension", "sdk"]), version: z.string().max(40) }),
    /** SDK only: the host app's user, shown as the reporter label. */
    reporter: z
      .object({ id: z.string().max(200).optional(), name: z.string().max(200).optional() })
      .optional(),
  })
  .refine((payload) => Object.keys(payload.context).length <= 50, {
    message: "At most 50 context keys",
    path: ["context"],
  })
  .refine(
    ({ video }) =>
      !video ||
      video.trimStartMs === undefined ||
      video.trimEndMs === undefined ||
      video.trimStartMs < video.trimEndMs,
    { message: "Trim start must be before trim end", path: ["video"] },
  )
  .refine(
    ({ video }) =>
      !video ||
      ((video.trimStartMs ?? 0) < video.durationMs && (video.trimEndMs ?? 0) <= video.durationMs),
    { message: "The trim must fall inside the video", path: ["video"] },
  )
  .refine(({ capture }) => !capture || capture.endedAt >= capture.startedAt, {
    message: "Capture must end after it starts",
    path: ["capture"],
  });

/** Multipart file parts: field name, accepted file names (the extension says the encoding). */
export const REPORT_FILE_PARTS = {
  screenshot: ["screenshot.webp", "screenshot.png"],
  thumbnail: ["thumbnail.webp"],
  video: ["video.webm"],
  replay: ["replay.json.br", "replay.json.gz"],
  console: ["console.json.br", "console.json.gz"],
  network: ["network.json.br", "network.json.gz"],
  actions: ["actions.json.br", "actions.json.gz"],
} as const;

export type ReportFilePart = keyof typeof REPORT_FILE_PARTS;

/** 201 (new) or 200 (same Idempotency-Key seen before). */
export const createdReportSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  number: z.number().int(),
  url: z.string().url(),
});

export type ReportKind = z.infer<typeof reportKindSchema>;
export type Priority = z.infer<typeof prioritySchema>;
export type JiraState = z.infer<typeof jiraStateSchema>;
export type ReportStatus = z.infer<typeof reportStatusSchema>;
export type ReportPayload = z.infer<typeof reportPayloadSchema>;
export type CreatedReport = z.infer<typeof createdReportSchema>;

const personSchema = z.object({ id: z.string(), name: z.string() });

/** FR-INB-01: one inbox row. */
export const reportSummarySchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  number: z.number().int(),
  projectId: z.string().uuid(),
  kind: reportKindSchema,
  title: z.string(),
  status: reportStatusSchema,
  priority: prioritySchema,
  consoleErrorCount: z.number().int(),
  failedRequestCount: z.number().int(),
  jiraKey: z.string().nullable(),
  reporter: personSchema.nullable(),
  /** SDK reports: the host app's user. */
  reporterLabel: z.string().nullable(),
  assignee: personSchema.nullable(),
  pageUrl: z.string(),
  hasThumbnail: z.boolean(),
  createdAt: z.string(),
});

export const fileKindSchema = z.enum([
  "screenshot",
  "thumbnail",
  "video",
  "replay",
  "console",
  "network",
  "actions",
]);

/** FR-VW-*: everything the viewer needs except the capture files, which load per tab. */
/** FR-JIRA-08: what the report shows about its Jira issue while there is none yet. */
export const jiraStateSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("none") }),
  z.object({ state: z.literal("pending") }),
  z.object({ state: z.literal("failed"), message: z.string(), failedAt: z.string() }),
  z.object({ state: z.literal("created"), key: z.string(), url: z.string() }),
]);

export const reportDetailSchema = reportSummarySchema.extend({
  description: z.string().nullable(),
  pageTitle: z.string(),
  browser: z.string(),
  os: z.string(),
  viewport: z.string(),
  device: z.record(z.string(), z.unknown()),
  context: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  captureMeta: z.object({
    truncatedReplay: z.boolean().optional(),
    captureErrors: z.number().optional(),
  }),
  requestIds: z.array(z.string()),
  client: z.object({ name: z.string(), version: z.string() }),
  capturedFrom: z.string().nullable(),
  capturedTo: z.string().nullable(),
  video: z
    .object({
      durationMs: z.number(),
      trimStartMs: z.number().nullable(),
      trimEndMs: z.number().nullable(),
    })
    .nullable(),
  files: z.array(
    z.object({
      kind: fileKindSchema,
      sizeBytes: z.number(),
      contentType: z.string(),
      url: z.string(),
    }),
  ),
  project: z.object({ id: z.string().uuid(), name: z.string(), keyPrefix: z.string() }),
  jiraUrl: z.string().nullable(),
  jira: jiraStateSchema,
  updatedAt: z.string(),
  resolvedAt: z.string().nullable(),
  expiredAt: z.string().nullable(),
});

/** `?status=open,in_progress` or repeated params, as a list. */
const csv = <T extends z.ZodType>(item: T) =>
  z.preprocess(
    (value) => (typeof value === "string" ? value.split(",").filter(Boolean) : value),
    z.array(item),
  );

const booleanQuery = z.enum(["true", "false"]).transform((value) => value === "true");

/** FR-INB-02, FR-INB-03: GET /workspaces/:ws/reports. */
export const reportListQuerySchema = z.object({
  view: z.enum(["inbox", "mine", "resolved", "all"]).default("inbox"),
  projectId: z.string().uuid().optional(),
  status: csv(reportStatusSchema).optional(),
  priority: csv(prioritySchema).optional(),
  kind: csv(reportKindSchema).optional(),
  reporterId: z.string().max(100).optional(),
  assigneeId: z.string().max(100).optional(),
  hasFailedRequests: booleanQuery.optional(),
  q: z.string().trim().max(200).optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** FR-INB-05: the inbox cards. */
export const reportCountsSchema = z.object({
  open: z.number().int(),
  /** Open reports assigned to the caller (sidebar). */
  mine: z.number().int(),
  /** Open reports created in the last 7 days. */
  newThisWeek: z.number().int(),
  withFailedRequests: z.number().int(),
  sentToJira: z.number().int(),
  resolvedThisWeek: z.number().int(),
});

export type ReportSummary = z.infer<typeof reportSummarySchema>;
export type ReportDetail = z.infer<typeof reportDetailSchema>;
export type FileKind = z.infer<typeof fileKindSchema>;
export type ReportListQuery = z.infer<typeof reportListQuerySchema>;
export type ReportCounts = z.infer<typeof reportCountsSchema>;

/** FR-VW-07: PATCH /reports/:ref. Only the fields sent change. */
export const reportUpdateSchema = z
  .object({
    title: z.string().trim().min(1, "Add a title").max(200),
    status: reportStatusSchema.exclude(["expired"]),
    priority: prioritySchema,
    assigneeId: z.string().min(1).max(100).nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, "Change at least one field");

/** FR-INB-04: POST /reports/bulk. */
/** Bulk ids, duplicates removed (the same report twice is one change). */
const bulkIds = z
  .array(z.string().uuid())
  .min(1)
  .max(100)
  .transform((ids) => [...new Set(ids)]);

export const reportBulkSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("status"),
    ids: bulkIds,
    value: reportStatusSchema.exclude(["expired"]),
  }),
  z.object({
    action: z.literal("priority"),
    ids: bulkIds,
    value: prioritySchema,
  }),
  z.object({
    action: z.literal("assign"),
    ids: bulkIds,
    value: z.string().min(1).max(100).nullable(),
  }),
  z.object({ action: z.literal("delete"), ids: bulkIds }),
  z.object({ action: z.literal("jira"), ids: bulkIds }),
]);

/** Which reports changed and which were left alone (not found or not allowed). */
export const bulkResultSchema = z.object({
  updated: z.array(z.string()),
  skipped: z.array(z.string()),
});

export type ReportUpdate = z.infer<typeof reportUpdateSchema>;
export type ReportBulk = z.infer<typeof reportBulkSchema>;
export type BulkResult = z.infer<typeof bulkResultSchema>;

/** FR-COL-01: a comment. `mentions` are user ids the client resolved from @names. */
export const commentSchema = z.object({
  id: z.string().uuid(),
  body: z.string(),
  author: personSchema.nullable(),
  mentions: z.array(personSchema),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
});

export const commentCreateSchema = z.object({
  body: z.string().trim().min(1, "Write something").max(10_000),
  mentions: z.array(z.string().min(1).max(100)).max(20).default([]),
});

export const commentUpdateSchema = commentCreateSchema;

/** FR-COL-02: one timeline entry. */
export const activityEventSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  actor: personSchema.nullable(),
  data: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});

export type Comment = z.infer<typeof commentSchema>;
export type CommentCreate = z.infer<typeof commentCreateSchema>;
export type ActivityEvent = z.infer<typeof activityEventSchema>;

/** FR-COL-03: a public link. The token is in `url` only when it is created. */
export const shareLinkSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  expiresAt: z.string(),
  createdBy: personSchema.nullable(),
});

export const shareLinkCreateSchema = z.object({
  expiresInDays: z.number().int().min(1).max(30).default(7),
});

export const createdShareLinkSchema = shareLinkSchema.extend({ url: z.string().url() });

export type ShareLink = z.infer<typeof shareLinkSchema>;
export type CreatedShareLink = z.infer<typeof createdShareLinkSchema>;
